// Added by Sejune Oh on 2019-06-25
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { RefObject, FC } from 'react'
import { createStyles, withStyles, WithStyles, Theme, WithTheme, Fade } from '@material-ui/core'
import classNames from 'classnames'
import { RouteComponentProps } from 'react-router'
import { AxiosError } from 'axios'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import { ReactEmptyEvent, routerPath, ReactClickEvent } from '@/helper/define'

// Intl
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Prime Elements
import { Snackbar, SnackbarContent } from '@/components/_primes'

// Components
import Dialog from '../../Dialog'

// Style
import MgmtFormTemplateStyle from '@/styles/management/MgmtFormTemplateStyle'
import { SNACK_BAR_VALID_TIME, MGMT_FORM_DIALOG_WIDTH } from '@/styles/Theme'
import { SettingTemplate } from '../SettingTemplate'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
import { MgmtSubInfoProps } from './MgmtFormTemplateContainer'

interface MgmtFormTemplateFCProps extends WithStyles<typeof styles>, WithTheme, RouteComponentProps {
    isErrorOccured: boolean
    errMessage: string
    onSnackbarClose: ReactEmptyEvent
    childComponentMounted: boolean
    dataNotFounded: boolean
    dialogRef: RefObject<Dialog>
    onDialogConfirm: () => Promise<{}>
    onDialogCancel: () => Promise<{}>
    customSnackbarValidTime?: number
    curPath: Array<[string, string]>
    isErrNoti: boolean
    isSubInfoVisible: boolean
    subInfos: Array<MgmtSubInfoProps>
    onClickSubInfo: ReactClickEvent
}

// Mgmt Form Template 하위 Component를 위한 공통 상속.
export interface MgmtFormChildrenCProps {
    onChildComponentMount: (key: string) => void
    showSnackbar?: (type: 'err' | 'info', message: Error | AxiosError | string) => void
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const MgmtFormTemplate: FC<MgmtFormTemplateFCProps> = ({
    classes,
    curPath,
    customSnackbarValidTime = SNACK_BAR_VALID_TIME,
    ...props
}) => {
    const mgmtText = Intl.getIntlText({ id: 'htmljs_react.mgmt.mgmtformtemplate.bread_crumb_text' })
    const mgmtPath = `/${routerPath.MGMT}`

    return (
        <SettingTemplate pageDepth={[[mgmtText, mgmtPath], ...curPath]} hideSearch={true}>
            {props.children}
            {props.isSubInfoVisible && props.subInfos.length > 0 && (
                <div data-cy-div={'information'}>
                    <div className={classes.SubInfoTitle}>Additional Information</div>
                    {props.subInfos.map(({ id, value }, index) => (
                        <div className={classes.SubInfo} key={`w-${index}`}>
                            <span key={`i-${index}`}>{id}: </span>
                            <span key={`v-${index}`} onClick={props.onClickSubInfo} data-target-id={`v-i-${index}`}>
                                {value}
                                <input
                                    id={`v-i-${index}`}
                                    key={`v-i-${index}`}
                                    type="string"
                                    value={value}
                                    onChange={() => null}
                                />
                            </span>
                        </div>
                    ))}
                </div>
            )}
            <Snackbar
                className={classes.ErrNotiWrapper}
                open={props.isErrorOccured}
                onClose={props.onSnackbarClose}
                TransitionComponent={Fade}
                autoHideDuration={customSnackbarValidTime}
                disableWindowBlurListener={true}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <SnackbarContent
                    evtType="error"
                    className={classNames(props.isErrNoti ? classes.ErrNoti : classes.InfoNoti)}
                    message={props.errMessage}
                    onClose={props.onSnackbarClose}
                />
            </Snackbar>
            <Dialog
                ref={props.dialogRef}
                onConfirm={props.onDialogConfirm}
                onCancel={props.onDialogCancel}
                dialogPaperStyle={{ minWidth: MGMT_FORM_DIALOG_WIDTH }}
            />
        </SettingTemplate>
    )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Styles
const styles = (theme: Theme) =>
    createStyles({
        ...MgmtFormTemplateStyle(theme),
        SubInfo: {
            fontSize: '0.8rem',
            color: 'rgba(255, 255, 255, 0.3)',
            lineHeight: 1.5,
            display: 'flex',
            '&>span:first-child': {
                flex: 0.2,
            },
            '&>span:last-child': {
                flex: 0.8,
                cursor: 'pointer',
                '&>input': {
                    width: '5px',
                    height: '5px',
                    opacity: 0,
                    margin: 0,
                    padding: 0,
                },
            },
        },
        SubInfoTitle: {
            color: 'rgba(255, 255, 255, 0.3)',
            marginBottom: 6,
        },
    })

export default withStyles(styles, { withTheme: true })(MgmtFormTemplate)
